import { ComponentProps, forwardRef } from 'react';
import { Link } from 'react-router-dom';

import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/utils/styles';

import { LoadingDots } from './loading-dots';

const buttonVariants = cva(
  "break-words whitespace-normal cursor-pointer inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground shadow-xs hover:bg-primary/90',
        destructive:
          'bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        outline:
          'border bg-transparent/30 shadow-xs hover:bg-input/50 hover:text-secondary-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50',
        'outline-muted':
          'border bg-background shadow-xs hover:bg-muted hover:text-secondary-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/80',
        secondary: 'bg-secondary text-accent-foreground shadow-xs hover:bg-secondary/80',
        ghost:
          'hover:bg-primary hover:text-primary-foreground dark:hover:bg-secondary dark:text-foreground',
        'ghost-secondary':
          'hover:bg-secondary hover:text-accent-foreground dark:hover:bg-secondary/50',
        'ghost-muted': 'hover:bg-muted hover:text-secondary-foreground dark:hover:bg-muted/50',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'min-w-32 min-h-9 px-4 py-2 has-[>svg]:px-3',
        sm: 'min-h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'min-h-10 rounded-md px-6 py-2 has-[>svg]:px-4',
        icon: 'size-9',
        link: 'p-0',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

type ButtonProps = ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  } & {
    loading?: boolean;
    href?: string;
  };

export const Button = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const {
    className,
    variant,
    size,
    asChild = false,
    children,
    loading,
    disabled,
    type = 'button',
    href,
    ...rest
  } = props;

  if (href) {
    const Comp = asChild ? Slot : Link;
    const _disabled = Boolean(loading ?? disabled);
    const _className = cn(
      buttonVariants({ variant, size: variant === 'link' ? 'link' : size, className })
    );

    return (
      <Comp
        className={_className}
        data-slot="a"
        to={_disabled ? '#' : href}
        tabIndex={rest.tabIndex}
      >
        {children}
      </Comp>
    );
  }

  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      {...rest}
      type={type}
      className={cn(buttonVariants({ variant, size, className }))}
      disabled={loading ?? disabled}
      data-slot="button"
      ref={ref}
    >
      {loading ? <LoadingDots /> : children}
    </Comp>
  );
});
Button.displayName = 'Button';
